import { LifeArea } from '@griply/types';
import {
  buildTodayFilterDocument,
  getFilterDocument,
  getTagFilterDocument,
} from '@griply/utils';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSmartLists } from 'features/smart-lists';
import { useTags } from 'features/tags';
import { useMemo } from 'react';
import { FilterDocuments } from 'shared/constants';
import { useFilterSlices } from 'shared/contexts/filter-slices';
import { useToday } from 'shared/contexts/today';
import { useRelationships } from 'shared/hooks/use-relationships';
import { useUser } from 'shared/hooks/use-user';
import { DeriveTaskCountsContext } from 'shared/utils/derive-task-counts';

const inboxDocument = FilterDocuments.Inbox.defaultFilterDocument;

// Every input `deriveTaskCounts` needs except the task buckets themselves —
// kept separate so the filter documents can be built (and the completed-gate
// derived from them) before the completed subscription is enabled.
export const useTaskCountsContext = (): DeriveTaskCountsContext => {
  const user = useUser();
  const today = useToday();
  const goals = useGoals();
  const tags = useTags();
  const relationships = useRelationships();
  const lifeAreas = useLocalizedLifeAreas();
  const slices = useFilterSlices();
  const smartLists = useSmartLists();

  const todayDocument = useMemo(
    () => buildTodayFilterDocument(today, user?.settings),
    [today, user?.settings],
  );

  const smartListDocuments = useMemo(
    () =>
      smartLists.map((smartList) => ({
        id: smartList.id,
        document: getFilterDocument(smartList),
      })),
    [smartLists],
  );

  const tagDocuments = useMemo(
    () =>
      tags.map((tag) => ({ id: tag.id, document: getTagFilterDocument(tag) })),
    [tags],
  );

  return useMemo(
    () => ({
      todayDocument,
      inboxDocument,
      smartListDocuments,
      tagDocuments,
      goals,
      tags,
      relationships,
      lifeAreas: lifeAreas as LifeArea[],
      slices,
      user,
      today,
    }),
    [
      todayDocument,
      smartListDocuments,
      tagDocuments,
      goals,
      tags,
      relationships,
      lifeAreas,
      slices,
      user,
      today,
    ],
  );
};
